/**
 * quantityForecasts sagas
 * IMPORTANT: QuantityForecasts is a versioned model.
 * There is ony one allowed per entity. To simplify state management I have keyed quantityForecasts
 * by entityId
 */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import { stopSubmit } from 'redux-form';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    QUANTITY_FORECAST_CREATE_REQUESTED,
    QUANTITY_FORECASTS_LOAD_REQUESTED,
    QUANTITY_FORECAST_UPDATE_REQUESTED
} from './constants';

// Actions
import {
    succeededLoadQuantityForecasts,
    failedLoadQuantityForecasts,
    succeededCreateQuantityForecast,
    failedCreateQuantityForecast,
    succeededUpdateQuantityForecast,
    failedUpdateQuantityForecast,
    setQuantityForecast,
    setQuantityForecasts
} from './actions';
import { updateQueryString } from '../router/actions';
import { enqueueSnackbar } from '../notifications/actions';

// Utils
import axiosRetry from '../../utils/axiosRetry';

// Selectors
import { idTokenSelector } from '../auth/selectors';
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';

// Environment
const REACT_APP_API_URL: string = String(process.env.REACT_APP_API_URL);

type Action = {
    +type: string,
    +payload: Object
};

export function* getQuantityForecasts(action: Action): Saga<void> {
    const {
        payload: { entityId, seasonId }
    } = action;

    const organizationId = yield select(selectCurrentOrganizationId);
    const url = `${REACT_APP_API_URL}/v1/QuantityForecasts`;

    const config = {
        method: 'GET',
        url,
        params: {
            organizationId,
            filter: {
                require: {
                    $and: {
                        deletedAt: { $exists: false },
                        versionedAt: { $exists: false },
                        ...(seasonId ? { 'entity.seasonId': seasonId } : {}),
                        ...(entityId
                            ? {
                                  $or: [
                                      {
                                          'entity.path': {
                                              $like: `/${entityId}/%`
                                          }
                                      },
                                      {
                                          entityId
                                      }
                                  ]
                              }
                            : {})
                    }
                }
            }
        }
    };

    try {
        const response = yield call(axiosRetry, config);

        // Succeeded Load QuantityForecasts
        yield put(succeededLoadQuantityForecasts(response));

        // Set QuantityForecasts
        yield put(setQuantityForecasts(response.data));
    } catch (error) {
        yield put(failedLoadQuantityForecasts(error));
    }
}

export function* watchQuantityForecastsLoadRequested(): Saga<void> {
    yield takeLatest(QUANTITY_FORECASTS_LOAD_REQUESTED, getQuantityForecasts);
}

export function* createQuantityForecast(action: Action): Saga<void> {
    const {
        payload: { data }
    } = action;

    const idToken = yield select(idTokenSelector);
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/QuantityForecasts`;

    const config = {
        method: 'post',
        url,
        headers: {
            Authorization: idToken
        },
        params: {
            organizationId
        },
        data
    };

    try {
        const response = yield call(axios, config);

        // Succeeded Create Quantity Forecast
        yield put(succeededCreateQuantityForecast(response));

        // Set Quantity Forecast
        yield put(setQuantityForecast(response.data));

        // Update Query String
        yield put(
            updateQueryString({
                forecastModal: undefined,
                selectedEntity: undefined
            })
        );

        yield put(
            enqueueSnackbar({
                message: 'Bin forecast created successfully',
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        // Failed Create Quantity Forecast
        yield put(failedCreateQuantityForecast(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('forecastForm', { _error: message }));
    }
}

export function* watchQuantityForecastsCreateRequested(): Saga<void> {
    yield takeLatest(
        QUANTITY_FORECAST_CREATE_REQUESTED,
        createQuantityForecast
    );
}

export function* updateQuantityForecast(action: Action): Saga<void> {
    const { quantityForecastId, data } = action.payload;

    const idToken = yield select(idTokenSelector);
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/QuantityForecasts/${quantityForecastId}`;

    const config = {
        method: 'patch',
        url,
        headers: {
            Authorization: idToken
        },
        params: {
            organizationId
        },
        data
    };

    try {
        const response = yield call(axios, config);

        // Succeeded Update Quantity Forecast
        yield put(succeededUpdateQuantityForecast(response));

        // Set Quantity Forecast
        yield put(setQuantityForecast(response.data));

        // Update Query String
        yield put(
            updateQueryString({
                forecastModal: undefined,
                selectedEntity: undefined
            })
        );

        yield put(
            enqueueSnackbar({
                message: 'Bin forecast updated successfully',
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        // Failed Update Quantity Forecast
        yield put(failedUpdateQuantityForecast(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('forecastForm', { _error: message }));
    }
}

export function* watchQuantityForecastsUpdateRequested(): Saga<void> {
    yield takeLatest(
        QUANTITY_FORECAST_UPDATE_REQUESTED,
        updateQuantityForecast
    );
}
